import moment, { Moment } from 'moment';
import React, { createContext, useMemo } from 'react';
import { useState } from 'react';
import './SingleCalendar.scss';
import Days from './Days';
import Heading from './Heading';
import { IRange } from '../../modules/location/details/models/locationDetails';
import getClientsStyle from '../../utils/getClientsStyle';

interface ISingleCalendarProps {
  dateChoice: string | null;
  firstRangeStart?: IRange;
  firstRangeEnd?: IRange;
  secondRangeStart?: IRange;
  secondRangeEnd?: IRange;
  onDateChange?: (date: string) => void;

  minDateISO?: string | null; // 'YYYY-MM-DD'
  maxDateISO?: string | null; // 'YYYY-MM-DD'
  hideNavigation?: boolean;
}

interface IDaysContext {
  today: Moment;
  selectedMonth: Moment | null;
  dateChoice: Moment | null;
  firstRangeStart: IRange | null;
  firstRangeEnd: IRange | null;
  secondRangeStart: IRange | null;
  secondRangeEnd: IRange | null;
  onClick: (date: string) => void;
  isDayDisabled: (date: Moment) => boolean;
}

export const DaysContext = createContext<Partial<IDaysContext>>({});

const SingleCalendar = (props: ISingleCalendarProps) => {
  const {
    firstRangeStart,
    firstRangeEnd,
    secondRangeStart,
    secondRangeEnd,
    onDateChange,
    minDateISO,
    maxDateISO,
    hideNavigation,
  } = props;

  const today = moment();

  const [date, setDate] = useState<Moment>(() =>
    minDateISO ? moment(minDateISO, 'YYYY-MM-DD') : today
  );
  const [dateChoice, setDateChoice] = useState<string | null>(props.dateChoice);
  const styleForClient = getClientsStyle();

  const isDayDisabled = useMemo(() => {
    const min = minDateISO ? moment(minDateISO, 'YYYY-MM-DD') : null;
    const max = maxDateISO ? moment(maxDateISO, 'YYYY-MM-DD') : null;
    return (d: Moment) => {
      if (min && d.isBefore(min, 'day')) return true;
      if (max && d.isAfter(max, 'day')) return true;
      return false;
    };
  }, [minDateISO, maxDateISO]);

  const resetDate = () => {
    setDate(minDateISO ? moment(minDateISO, 'YYYY-MM-DD') : today);
  };

  const changeMonth = (month: number) => {
    setDate((prev) => prev.clone().month(month));
  };

  const changeDate = (dateStr: string) => {
    if (isDayDisabled(moment(dateStr, 'YYYY-MM-DD'))) return;
    setDateChoice(dateStr);
    onDateChange?.(moment(dateStr, 'YYYY-MM-DD').format('YYYY-MM-DD'));
  };

  return (
    <div
      className={`berg-components-single-calendar${hideNavigation ? ' berg-components-single-calendar--compact' : ''}`}
      data-client-style={styleForClient}
    >
      <Heading
        date={date}
        changeMonth={changeMonth}
        resetDate={() => resetDate()}
        hideNavigation={hideNavigation}
      />
      <DaysContext.Provider
        value={{
          today,
          selectedMonth: date,
          dateChoice: dateChoice ? moment(dateChoice, 'YYYY-MM-DD') : null,
          firstRangeStart,
          firstRangeEnd,
          secondRangeStart,
          secondRangeEnd,
          onClick: changeDate,
          isDayDisabled,
        }}
      >
        <Days date={date} today={today} />
      </DaysContext.Provider>
    </div>
  );
};

export default SingleCalendar;
